import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import mixpanel from '~/components/CmsMain/mixpanel';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { COMPLEXITY_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import COMPLEXITY_VALUES from '~/server_shared/generated-types/COMPLEXITY_VALUES';
import { reportAxiosError } from '~/Utils';

type ComplexityEditDialogProps = {
  claimId: number;
  currentComplexity?: string | null;
  currentReason?: string | null;
  onClose: () => void;
  onUpdate: (update: { complexity: string; complexity_reason: string | null }) => void | Promise<void>;
  baseMixpanelProps?: Record<string, unknown>;
};

const COMPLEXITY_DISMISSAL_METHODS = {
  CANCEL_BUTTON: 'cancel_button',
  CLOSE: 'close',
};

const ComplexityEditDialog: React.FC<ComplexityEditDialogProps> = ({
  claimId,
  currentComplexity,
  currentReason,
  onClose,
  onUpdate,
  baseMixpanelProps = {},
}) => {
  const [complexity, setComplexity] = useState(currentComplexity?.toLowerCase() ?? '');
  const [reason, setReason] = useState(currentReason ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSaveDisabled = !complexity || isSubmitting;

  const previousComplexity = currentComplexity?.toLowerCase() ?? null;
  const previousReason = currentReason ?? '';
  const hasUnsavedChanges = complexity !== (previousComplexity ?? '') || reason !== previousReason;

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const update = { complexity, complexity_reason: reason.trim() || null };
      await axios.patch(`/api/v1/claims/${claimId}/complexity`, update);
      await onUpdate(update);
      mixpanel.track(COMPLEXITY_MIXPANEL_EVENTS.COMPLEXITY_UPDATED, {
        ...baseMixpanelProps,
        previous_complexity: previousComplexity,
        new_complexity: complexity,
        previous_reason_length: previousReason.length,
        new_reason_length: (update.complexity_reason ?? '').length,
        reason_changed: (update.complexity_reason ?? '') !== previousReason,
      });
      onClose();
    } catch (error) {
      mixpanel.track(COMPLEXITY_MIXPANEL_EVENTS.COMPLEXITY_UPDATE_FAILED, {
        ...baseMixpanelProps,
        attempted_complexity: complexity,
        error_status: axios.isAxiosError(error) ? (error.response?.status ?? null) : null,
        error_message: error instanceof Error ? error.message : String(error),
      });
      reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const dismissDialog = (dismissalMethod: string) => {
    mixpanel.track(COMPLEXITY_MIXPANEL_EVENTS.COMPLEXITY_DIALOG_DISMISSED, {
      ...baseMixpanelProps,
      dismissal_method: dismissalMethod,
      had_unsaved_changes: hasUnsavedChanges,
    });
    onClose();
  };

  return (
    <CardDialog
      isDialog
      open
      title="Claim Complexity"
      onClose={() => dismissDialog(COMPLEXITY_DISMISSAL_METHODS.CLOSE)}
      maxWidth="sm"
      fullWidth
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSave}
          onClickSecondary={() => dismissDialog(COMPLEXITY_DISMISSAL_METHODS.CANCEL_BUTTON)}
          disabled={isSaveDisabled}
        />
      }
    >
      <div className="mt-8 flex flex-col gap-32">
        <TextField
          select
          label="Claim Complexity"
          value={complexity}
          onChange={(value: string) => setComplexity(value)}
          fullWidth
        >
          {Object.entries(COMPLEXITY_VALUES).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Reason"
          value={reason}
          onChange={(value: string) => setReason(value)}
          multiline
          rows={2}
          fullWidth
        />
      </div>
    </CardDialog>
  );
};

export default ComplexityEditDialog;
